import React, { Dispatch, SetStateAction } from 'react';
import { Form } from 'antd';
import intl from 'react-intl-universal';
import { get } from 'lodash';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

import { itemMap } from './Items';
import { QuestionItem, QuestionFormItemType } from '../../services/model';
import './index.less';

const { Item } = Form;

/**
 * 表单项组件属性
 */
interface FormItemProps {
  ruleCheckVisible?: boolean; // 是否显示
  item: QuestionItem; // 题目数据
  isChild?: boolean; // 是否为子题目
  errorList?: { [key: string]: string }; // 错误列表
  setErrorList?: Dispatch<SetStateAction<{ [key: string]: string }>>; // 设置错误列表
}

/**
 * 滚动到指定元素
 * 用于表单校验错误时的自动滚动
 */
export const scrollToview = (elem?: HTMLElement | null) => {
  if (elem) {
    scrollIntoViewIfNeeded(elem, {
      scrollMode: 'if-needed',
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });
  }
};

/**
 * 表单项组件
 * 负责渲染单个题目，包含各种题型的处理逻辑
 */
export const FormItem: React.FC<FormItemProps> = (props) => {
  const { item, isChild, errorList, setErrorList, ruleCheckVisible = true } = props;
  const [form] = Form.useForm();

  // 获取题目相关信息
  const typeName = item.type.type; // 题目类型
  const Comp = itemMap[typeName] || null; // 对应的组件
  const id = item.object_id; // 题目ID
  const fieldLabel = get(item, 'name.i18n_name', ''); // 题目标签
  const desc = get(item, 'description.i18n_name', ''); // 题目描述
  const isMultipleSelect = typeName === QuestionFormItemType.multi_select; // 是否为多选题
  const childList = get(item, 'children_list', []); // 子题目列表

  // 如果是问卷描述类型，直接渲染描述文本
  if (typeName === QuestionFormItemType.questionnaire_description) {
    return <div className="questionnaire_description--mobile">{fieldLabel}</div>;
  }

  // 标签组件
  const label = (
    <p>
      {fieldLabel}
      {isMultipleSelect && (
        <span className="mobile__detail__content__questionnaireDetail__form__multiSelect-label-desc">
          {intl.get('multiSelect')}
        </span>
      )}
    </p>
  );

  /**
   * 处理焦点事件
   * 清除当前字段的错误状态
   */
  const handleFocus = () => {
    form.setFields([
      {
        touched: true,
        validating: false,
        value: form.getFieldValue(id),
        name: id,
        errors: []
      }
    ]);
    if (errorList?.[id]) {
      setErrorList &&
        setErrorList(() => {
          const restList = { ...errorList };
          delete restList[id];
          return restList;
        });
    }
  };

  /**
   * 处理重置错误
   * 清除当前字段的错误状态
   */
  const handleResetError = () => {
    form.setFields([
      {
        touched: true,
        validating: false,
        value: form.getFieldValue(id),
        name: id,
        errors: []
      }
    ]);
    if (errorList?.[id]) {
      setErrorList &&
        setErrorList(() => {
          const restList = { ...errorList };
          delete restList[id];
          return restList;
        });
    }
  };

  /**
   * 渲染表单项
   * 根据题目类型渲染对应的组件
   */
  return (
    <Item
      key={id}
      name={id}
      label={label}
      validateTrigger={'onSubmit'}
      rules={
        ruleCheckVisible
          ? [
              {
                required: item.is_required,
                message: intl.get('requiredError', { fieldLabel })
              }
            ]
          : []
      } // 根据可见性决定是否应用校验规则
      style={{ display: ruleCheckVisible ? 'block' : 'none' }} // 根据可见性控制显示
      className={typeName === QuestionFormItemType.five_star_scoring ? 'is_five_star_scoring' : ''}
    >
      {/* 渲染对应的题目组件 */}
      {Comp && (
        <Comp
          config={get(item, 'type.config')} // 组件配置
          desc={desc} // 描述信息
          isChild={isChild} // 是否为子题目
          childList={childList} // 子题目列表
          errorList={errorList} // 错误列表
          setErrorList={setErrorList} // 设置错误列表
          onFocus={handleFocus} // 焦点事件处理
          resetError={handleResetError} // 重置错误处理
          errorMsg={errorList?.[id]} // 当前字段错误信息
          scrollToview={scrollToview} // 滚动到视图函数
        />
      )}
    </Item>
  );
};
